import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import { ROLES } from '@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.constants'
import { LocalPaymentMethodReference } from '@backmarket/nuxt-layer-payment/methods/config/methods.type'

import { ROUTE_NAMES } from '~/constants/routes'
import OptionsTranslations from '~/scopes/buyback/pages/options/Options.translations'
import OrdersTranslations from '~/scopes/buyback/pages/orders/Orders.translations'

const routes = (): RouteRecordRaw[] => {
  const config = useRuntimeConfig()

  return [
    {
      name: ROUTE_NAMES.BUYBACK.ORDERS.INDEX,
      component: () => import('./pages/orders/Orders.vue'),
      path: '/bo-seller/buyback/orders',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'orders',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
      children: [
        {
          name: ROUTE_NAMES.BUYBACK.ORDERS.ACTIVE,
          component: () => import('./pages/orders/components/Tab/Tab.vue'),
          path: 'active',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'orders.active',
            tabItem: {
              label: OrdersTranslations.newOrders,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.ORDERS.PENDING,
          component: () => import('./pages/orders/components/Tab/Tab.vue'),
          path: 'pending',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'orders.pending',
            tabItem: {
              label: OrdersTranslations.pendingCustomers,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.ORDERS.ALL,
          component: () => import('./pages/orders/components/Tab/Tab.vue'),
          path: 'all',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'orders.all',
            tabItem: {
              label: OrdersTranslations.allOrders,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
      ],
    },
    {
      name: ROUTE_NAMES.BUYBACK.ORDERS.DETAILS,
      component: () => import('./pages/orderDetails/OrderDetails.vue'),
      path: '/bo-seller/buyback/orders/:id',
      meta: {
        isBuyback: true,
        disableClientSideNavigation: () => true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.ORDERS.CONVERSATION,
      path: '/bo-seller/buyback/orders/:id/conversation',
      component: () =>
        import(
          '@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.vue'
        ),
      props: {
        role: ROLES.MERCHANT,
      },
      meta: {
        layout: 'buyback-conversation',
        isBuyback: true,
        disableClientSideNavigation: () => true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION,
      component: () =>
        config.public.FF_BO_SELLER_IS_BUYBACK_QUOTES_SERVICE_OPEN
          ? import('./pages/quotations/CreateProductQuotes.vue')
          : import('./pages/quotations/CreateProductQuotations.vue'),
      path: '/bo-seller/buyback/create-quotation/:productId?',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'create-quotation',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION_QA,
      component: () => import('./pages/quotations/CreateProductQuotes.vue'),
      path: '/bo-seller/buyback/create-quotation-qa/:productId?',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'create-quotation',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
      component: () => import('./pages/search-product/SearchProduct.vue'),
      path: '/bo-seller/buyback/search-product',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'search-product',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.WISHLIST,
      component: () =>
        config.public.FF_BO_SELLER_IS_BUYBACK_QUOTES_SERVICE_OPEN
          ? import('./pages/wishlist/QuotesWishList.vue')
          : import('./pages/wishlist/WishList.vue'),
      path: '/bo-seller/buyback/wishlist',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'wishlist',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.WISHLIST_QA,
      component: () => import('./pages/wishlist/QuotesWishList.vue'),
      path: '/bo-seller/buyback/wishlist-qa',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'wishlist',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.OPTIONS.INDEX,
      ...(config.public.FF_BO_SELLER_IS_BUYBACK_OPTIONS_OPEN
        ? {
            component: () => import('./pages/options/Options.vue'),
            redirect: { name: ROUTE_NAMES.BUYBACK.OPTIONS.ADDRESSES },
          }
        : {
            redirect: {
              name: ROUTE_NAMES.BADOOM.SOURCING.OPTIONS.INDEX,
            },
          }),
      path: '/bo-seller/buyback/options',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'options',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
      children: [
        {
          name: ROUTE_NAMES.BUYBACK.OPTIONS.ADDRESSES,
          ...(config.public.FF_BO_SELLER_IS_BUYBACK_OPTIONS_OPEN
            ? {
                component: () =>
                  import('./pages/options/components/Addresses/Addresses.vue'),
              }
            : {
                redirect: {
                  name: ROUTE_NAMES.BADOOM.SOURCING.OPTIONS.INDEX,
                },
              }),
          path: 'addresses',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'options.addresses',
            tabItem: {
              label: OptionsTranslations.adressesTabTitle,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.OPTIONS.PAYMENT,
          ...(config.public.FF_BO_SELLER_IS_BUYBACK_OPTIONS_OPEN
            ? {
                component: () =>
                  import('./pages/options/components/Payment/Payment.vue'),
              }
            : {
                redirect: {
                  name: ROUTE_NAMES.BADOOM.SOURCING.OPTIONS.INDEX,
                },
              }),
          path: 'payment',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'options.payment',
            tabItem: {
              label: OptionsTranslations.paymentTabTitle,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.OPTIONS.PRICING_RULES,
          ...(config.public.FF_BO_SELLER_IS_BUYBACK_OPTIONS_OPEN
            ? {
                component: () =>
                  import(
                    './pages/options/components/PricingRules/PricingRules.vue'
                  ),
              }
            : {
                redirect: {
                  name: ROUTE_NAMES.BADOOM.SOURCING.OPTIONS.INDEX,
                },
              }),
          path: 'pricing-rules',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'options.pricing-rules',
            tabItem: {
              label: OptionsTranslations.pricingRulesTabTitle,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
      ],
    },
    {
      name: ROUTE_NAMES.BUYBACK.OPTIONS.UPDATE_PAYMENT_METHOD,
      component: () =>
        import('./pages/update-payment-method-v2/UpdatePaymentMethod.vue'),
      path: '/bo-seller/buyback/options/payment/:code',
      meta: {
        isBuyback: true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.UPDATE_PAYMENT_METHOD_CARD,
      component: () =>
        import('./pages/update-payment-method/UpdatePaymentMethod.vue'),
      props: {
        paymentMethodReference: LocalPaymentMethodReference.LEGACY_CARD,
      },
      path: '/bo-seller/buyback/update-payment-card',
      meta: {
        isBuyback: true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.UPDATE_PAYMENT_METHOD_SEPA,
      component: () =>
        import('./pages/update-payment-method/UpdatePaymentMethod.vue'),
      props: {
        paymentMethodReference: LocalPaymentMethodReference.SEPA,
      },
      path: '/bo-seller/buyback/update-payment-sepa',
      meta: {
        isBuyback: true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.UPDATE_PAYMENT_METHOD_BACS,
      component: () =>
        import('./pages/update-payment-method/UpdatePaymentMethod.vue'),
      props: {
        paymentMethodReference: LocalPaymentMethodReference.BACS,
      },
      path: '/bo-seller/buyback/update-payment-bacs',
      meta: {
        isBuyback: true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.UPDATE_PAYMENT_METHOD_ACH,
      component: () =>
        import('./pages/update-payment-method/UpdatePaymentMethod.vue'),
      props: {
        paymentMethodReference: LocalPaymentMethodReference.ACH,
      },
      path: '/bo-seller/buyback/update-payment-ach',
      meta: {
        isBuyback: true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
  ]
}

export default routes
